<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h2>{{ $t('raIncomingMail.upload.title') }}</h2>
        <button @click="$emit('close')" class="close-btn" :disabled="submitting">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <form @submit.prevent="submitForm" class="upload-form">
        <div v-if="error" class="error-message">
          <i class="fas fa-exclamation-circle"></i>
          {{ error }}
          <ul v-if="Array.isArray(errorDetails) && errorDetails.length > 0" class="error-details">
            <li v-for="(detail, idx) in errorDetails" :key="idx">{{ detail }}</li>
          </ul>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="business">{{ $t('raIncomingMail.upload.form.business.label') }} <span class="required">*</span></label>
            <select v-model="form.businessId" id="business" required :disabled="submitting">
              <option value="">{{ $t('raIncomingMail.upload.form.business.placeholder') }}</option>
              <option v-for="business in businesses" :key="business.id" :value="business.id">
                {{ business.legalBusinessName || business.tradeName || $t('raIncomingMail.unnamedBusiness') }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label for="mailType">Mail Type <span class="required">*</span></label>
            <select v-model="form.mailType" id="mailType" required :disabled="submitting">
              <option value="">Select Type</option>
              <option value="Legal">Legal</option>
              <option value="Tax">Tax</option>
              <option value="General">General</option>
            </select>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label for="priority">Priority <span class="required">*</span></label>
            <select v-model="form.priority" id="priority" required :disabled="submitting">
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Urgent">Urgent</option>
            </select>
          </div>
          <div class="form-group">
            <label for="receivedDate">{{ $t('raIncomingMail.upload.form.receivedDate.label') }} <span class="required">*</span></label>
            <input type="date" id="receivedDate" v-model="form.receivedDate" required :disabled="submitting" />
          </div>
        </div>
        <div class="form-group">
          <label for="description">{{ $t('raIncomingMail.upload.form.description.label') }}</label>
          <textarea id="description" v-model="form.description" rows="2" :disabled="submitting" 
            :placeholder="$t('raIncomingMail.upload.form.description.placeholder')"></textarea>
        </div>
        <div class="form-group">
          <label for="file">{{ $t('raIncomingMail.upload.form.attachments.label') }} <span class="required">*</span></label>
          <div class="file-drop-area" @dragover.prevent="dragActive = true" @dragleave.prevent="dragActive = false"
            @drop.prevent="onDropFile" :class="{ 'drag-active': dragActive, 'disabled': submitting }">
            <input type="file" id="file" ref="fileInput" @change="onFileChange" 
              multiple accept="image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              :disabled="submitting" style="display:none;" />
            <div class="file-drop-content" @click="triggerFileInput">
              <i class="fas fa-cloud-upload-alt"></i>
              <h4>{{ $t('raIncomingMail.upload.form.attachments.dragDrop.title') }}</h4>
              <p>{{ $t('raIncomingMail.upload.form.attachments.dragDrop.or') }} <span class="file-browse">{{ $t('raIncomingMail.upload.form.attachments.dragDrop.browse') }}</span></p>
              <p class="file-hint">{{ $t('raIncomingMail.upload.form.attachments.dragDrop.hint') }}</p>
            </div>
            <div v-if="form.files.length > 0" class="selected-files">
              <div v-for="(file, idx) in form.files" :key="idx" class="selected-file-name">
                <i class="fas fa-file"></i>
                <div class="file-name-wrapper">
                  <span class="file-name" :title="file.name">{{ file.name }}</span>
                  <span class="file-size">({{ formatFileSize(file.size) }})</span>
                </div>
                <button type="button" class="remove-file-btn" @click="removeFile(idx)" :disabled="submitting" :title="$t('raIncomingMail.upload.buttons.remove')">
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="cancel-btn" :disabled="submitting">
            {{ $t('raIncomingMail.upload.buttons.cancel') }}
          </button>
          <button type="submit" class="upload-submit-btn" :disabled="submitting || !isFormValid">
            <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
            {{ submitting ? $t('raIncomingMail.upload.buttons.uploading') : $t('raIncomingMail.upload.buttons.upload') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import businessService from '@/services/businessService';
import incomingMailService from '@/services/incomingMailService';

export default {
  name: 'RAUploadMail',
  props: {
    currentLanguage: {
      type: String,
      default: 'English'
    }
  },
  data() {
    const now = new Date();
    const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return {
      businesses: [],
      submitting: false,
      dragActive: false,
      error: null,
      errorDetails: [],
      form: {
        businessId: '',
        mailType: '',
        priority: 'Medium',
        description: '',
        receivedDate: localDate.toISOString().split('T')[0],
        files: []
      }
    };
  },
  watch: {
    currentLanguage: {
      immediate: true,
      handler(newLanguage) {
        const lang = newLanguage === 'English' ? 'en' : 'es'
        this.$i18n.locale = lang
      }
    }
  },
  computed: {
    isFormValid() {
      return this.form.businessId && 
             this.form.mailType && 
             this.form.priority && 
             this.form.receivedDate &&
             this.form.files.length > 0;
    }
  },
  methods: {
    async fetchBusinesses() {
      try {
        const agentId = localStorage.getItem('agentId');
        const response = await businessService.getAllBusinesses(agentId);
        if (response && response.success) {
          this.businesses = Array.isArray(response.data) ? response.data : [];
        } else {
          this.businesses = [];
        }
      } catch (error) {
        this.businesses = [];
      }
    },
    triggerFileInput() {
      if (!this.submitting) {
        this.$refs.fileInput.click();
      }
    },    validateFiles(files) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const errors = [];

      for (const file of files) {
        if (!allowedTypes.includes(file.type)) {
          errors.push(this.$t('raIncomingMail.upload.validation.fileType', { fileName: file.name }));
        }
        if (file.size > maxSize) {
          errors.push(this.$t('raIncomingMail.upload.validation.fileSize', { fileName: file.name }));
        }
      }

      return errors;
    },
    onFileChange(e) {
      if (this.submitting) return;
      const files = Array.from(e.target.files);
      const errors = this.validateFiles(files);
      
      if (errors.length > 0) {
        this.error = 'File validation failed:';
        this.errorDetails = errors;
        return;
      }

      this.error = null;
      this.errorDetails = [];
      this.form.files = files;
      this.dragActive = false;
    },
    onDropFile(e) {
      if (this.submitting) return;
      e.preventDefault();
      this.dragActive = false;
      
      const files = Array.from(e.dataTransfer.files);
      const errors = this.validateFiles(files);
      
      if (errors.length > 0) {
        this.error = 'File validation failed:';
        this.errorDetails = errors;
        return;
      }

      this.error = null;
      this.errorDetails = [];
      this.form.files = files;
    },
    removeFile(idx) {
      if (this.submitting) return;
      this.form.files.splice(idx, 1);
      this.$refs.fileInput.value = '';
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },
    async submitForm() {
      if (!this.isFormValid || this.submitting) return;
      
      this.submitting = true;
      this.error = null;
      this.errorDetails = [];
      try {
        const formData = new FormData();
        // Create a formatted date at midnight in local timezone
        const receivedDate = new Date(this.form.receivedDate);
        receivedDate.setHours(0, 0, 0, 0);
        
        Object.keys(this.form).forEach(key => {
          if (key === 'receivedDate') {
            formData.append(key, receivedDate.toISOString());
          } else if (key !== 'files' && this.form[key]) {
            formData.append(key, this.form[key]);
          }
        });
        
        (this.form.files || []).forEach(file => {
          formData.append('files', file);
        });

        const response = await incomingMailService.uploadIncomingMail(formData);
        
        if (response.success) {
          this.$emit('mail-uploaded', response.data);
          this.$emit('close');
        } else {
          this.error = response.error || this.$t('raIncomingMail.errors.uploadFailed');
          if (response.details) {
            this.errorDetails = Array.isArray(response.details) ? response.details : [response.details];
          }
        }
      } catch (error) {
        console.error('Error uploading mail:', error);
        this.error = error.message || 'Failed to upload mail';
        if (error.response?.data?.details) {
          this.errorDetails = Array.isArray(error.response.data.details) ? 
            error.response.data.details : [error.response.data.details];
        }
      } finally {
        this.submitting = false;
      }
    }
  },
  mounted() {
    this.fetchBusinesses();
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 12px;
  width: 95%;
  max-width: 600px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.modal-header {
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  color: #6b7280;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.close-btn:hover {
  background-color: #f3f4f6;
  color: #111827;
}

.upload-form {
  padding: 24px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.form-group select,
.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #fff;
  font-size: 0.925rem;
  color: #111827;
  transition: all 0.2s;
}

.form-group select:focus,
.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

.file-drop-area {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #f9fafb;
}

.file-drop-area:hover:not(.disabled),
.file-drop-area.drag-active {
  border-color: #4CAF50;
  background: #f0fdf4;
}

.file-drop-content i {
  font-size: 2rem;
  color: #4CAF50;
  margin-bottom: 12px;
}

.file-browse {
  color: #4CAF50;
  font-weight: 500;
}

.file-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 8px;
}

.selected-file-name {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f3f4f6;
  border-radius: 6px;
  margin-top: 8px;
  overflow: hidden;
}

.selected-file-name i {
  color: #4CAF50;
  margin-right: 8px;
  flex-shrink: 0;
}

.file-name-wrapper {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  margin-right: 8px;
}

.file-name {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.875rem;
}

.file-size {
  color: #6b7280;
  font-size: 0.75rem;
  margin-left: 4px;
}

.remove-file-btn {
  flex-shrink: 0;
  color: white;
  background-color: #ef4444;
  border: none;
  cursor: pointer;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.remove-file-btn i {
  color: white;
  font-size: 0.75rem;
  margin: 0;
}

.remove-file-btn:hover {
  background-color: #dc2626;
  transform: scale(1.05);
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.cancel-btn {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.upload-submit-btn {
  padding: 8px 20px;
  background: #4CAF50;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.upload-submit-btn:not(:disabled):hover {
  background: #43a047;
}

.upload-submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.error-message {
  background: #fee2e2;
  color: #b91c1c;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 0.875rem;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.error-message i {
  margin-top: 2px;
}

.error-details {
  margin: 8px 0 0 24px;
  padding-left: 16px;
  font-size: 0.8125rem;
}

.required {
  color: #ef4444;
  margin-left: 2px;
}
</style>